import { useState, useEffect } from 'react';

import { ATL_GENERAL } from '@confluence/web-item-location';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { usePageState } from '@confluence/page-context';
import { expVal } from '@confluence/feature-experiments';

import { useWebPanelLocation } from './useWebPanelLocation';

export const useATLGeneralWebPanelListener = () => {
	const [webPanelHeight, setWebPanelHeight] = useState(() => {
		if (
			typeof window !== 'undefined' &&
			Boolean(window.__SSR_RENDERED__) &&
			expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)
		) {
			const initialHeight = window['__ATL_GENERAL_WEBPANEL_INITIAL_HEIGHT__'];
			return initialHeight ?? 0;
		} else {
			return getAtlGeneralWebPanelHeight();
		}
	});
	const [{ contentId, spaceKey }] = usePageState();

	const { panels } = useWebPanelLocation({
		location: ATL_GENERAL,
		fetchPolicy: 'cache-first',
		spaceKey,
		contentId,
	});

	const isLivePage = useIsCurrentPageLive();

	const isATLGeneralWebPanelFixed = panels.length > 0 && !isLivePage;

	useEffect(() => {
		const listener = (e) => {
			if (expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)) {
				setWebPanelHeight(e?.detail?.height ?? 0);
			} else {
				setWebPanelHeight(getAtlGeneralWebPanelHeight());
			}
		};

		if (isATLGeneralWebPanelFixed) {
			document.addEventListener('atlGeneralWebPanelHeightUpdated', listener);
		}

		return () => {
			if (isATLGeneralWebPanelFixed) {
				document.removeEventListener('atlGeneralWebPanelHeightUpdated', listener);
			}
		};
	}, [isATLGeneralWebPanelFixed]);

	return {
		isATLGeneralWebPanelFixed,
		atlGeneralWebPanelHeight: webPanelHeight,
	};
};

const getAtlGeneralWebPanelHeight = () => {
	if (typeof document === 'undefined') {
		return 0;
	}

	return (
		parseInt(
			getComputedStyle(document.documentElement).getPropertyValue('--atl-general-webpanel-height'),
		) || 0
	);
};
